import {CommentIcon, XIcon} from '@primer/octicons-react'
import {IconButton, Spinner} from '@primer/react'
import {Dialog, Banner} from '@primer/react/experimental'
import {memo, Suspense, useEffect, useCallback, useMemo, useRef, useState} from 'react'
import {CommentsFilter, type CommentsFilterState, filterComments, getDefaultFilterState} from './CommentsFilter'
import {ThreadPreview} from './ThreadPreview'
import type {Author} from '@github-ui/diff-lines/conversation-types'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import type {PageLimits} from '../../page-data/payloads/files'
import {ZeroState} from './ZeroState'
import {useThreadPreviewsPageData, type ThreadPreviewsPayload} from '../../page-data/payloads/thread-previews'

interface CommentsSidePanelProps {
  onClose: () => void
  pageLimits: PageLimits
  pathName: string
  pullRequestId: string
  isOpen: boolean
  tabSize?: number
  filteredThreadIds: Set<string>
  setThreadPreviews: (threadPreviews: ThreadPreviewsPayload) => void
}

function CommentsSidePanelContent({
  filteredThreadIds,
  onClose,
  pageLimits,
  pathName,
  pullRequestId,
  isOpen,
  setThreadPreviews,
  tabSize,
}: CommentsSidePanelProps) {
  const {data: threadPreviews = []} = useThreadPreviewsPageData({
    pathName,
  })

  useEffect(() => {
    setThreadPreviews(threadPreviews)
  }, [threadPreviews, setThreadPreviews])

  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  const hasThreads = threadPreviews.length > 0
  const navigateToThread = useCallback(() => {
    onClose()
  }, [onClose])

  const threadSummaries = threadPreviews
    .map(thread => {
      if (!thread || !filteredThreadIds.has(thread.threadId)) return null
      return (
        <ThreadPreview
          key={thread.threadId}
          tabSize={tabSize}
          thread={thread}
          onNavigateToDiffComment={navigateToThread}
          pullRequestId={pullRequestId}
          headingLevel="h2"
        />
      )
    })
    .filter(Boolean)

  useEffect(() => {
    if (!isOpen) return
    const announceText =
      threadSummaries.length > 0
        ? `${threadSummaries.length} ${threadSummaries.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`
    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, debouncedAnnounce, threadSummaries])

  return (
    <>
      {pageLimits.reviewThreadsLimitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the first ${pageLimits.reviewThreadsLimit} comments are currently being shown.`}
          className="mb-3"
        />
      )}
      {threadSummaries.length > 0 ? (
        <Suspense fallback={<Spinner />}>
          <div className="d-flex flex-column position-relative width-full gap-3">{threadSummaries}</div>
        </Suspense>
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            description="Comments will show up here as soon as there are some."
            heading={hasThreads ? 'No comments match the current filter' : 'No comments on changes yet'}
            icon={CommentIcon}
            headingLevel="h2"
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}
/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads
 */
export const CommentsSidePanel = memo(function CommentsSidePanel(
  props: {
    toggleSidesheetRef: React.RefObject<HTMLElement>
  } & Omit<CommentsSidePanelProps, 'setThreadPreviews' | 'filteredThreadIds'>,
) {
  const {pathName, toggleSidesheetRef, ...rest} = props
  const [threadPreviews, setThreadPreviews] = useState<ThreadPreviewsPayload>([])
  const [filterState, setFilterState] = useState<CommentsFilterState>(() => getDefaultFilterState())
  const filteredThreadIds = filterComments(threadPreviews, filterState)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const authorList = useMemo(() => {
    const authors: Author[] = []

    for (const thread of threadPreviews) {
      const firstComment = thread?.firstComment
      if (firstComment?.author && !authors.some(author => author.login === firstComment.author?.login)) {
        // Copilot goes to the top of the filter
        if (firstComment.author.login === 'Copilot') {
          authors.unshift(firstComment.author)
        } else {
          authors.push(firstComment.author)
        }
      }
    }

    return authors
  }, [threadPreviews])
  if (!rest.isOpen) return null

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={rest.onClose}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header className="p-3">
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full">
            <Dialog.Title id={dialogLabelId}>Comments</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close comments panel"
              icon={XIcon}
              variant="invisible"
              onClick={rest.onClose}
            />
          </div>
          <CommentsFilter
            authorList={authorList}
            className="mt-2 width-full"
            filterState={filterState}
            onFilterStateChange={setFilterState}
          />
        </Dialog.Header>
      )}
    >
      <Suspense
        fallback={
          <div className="d-flex flex-justify-center">
            <Spinner />
          </div>
        }
      >
        <CommentsSidePanelContent
          {...rest}
          filteredThreadIds={filteredThreadIds}
          pathName={pathName}
          setThreadPreviews={setThreadPreviews}
        />
      </Suspense>
    </Dialog>
  )
})

try{ CommentsSidePanelContent.displayName ||= 'CommentsSidePanelContent' } catch {}
try{ CommentsSidePanel.displayName ||= 'CommentsSidePanel' } catch {}